import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, ExternalLink, DollarSign, Clock, BarChart3, Target, CheckCircle2, XCircle, Mail, CalendarDays, Presentation } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { TasksSection } from './components/TasksSection'
import { usePresalesDashboardStore } from './store'
import { usePresalesDashboardStore as useCalendarStore } from '@/store'
import {
  formatCurrency,
  formatDate,
  getSalesHealth,
  getPresalesHealth,
  getDealTypeBadge,
  getStatusInfo,
  type HealthResult,
} from './utils/dealHelpers'

export function PresalesDealDetailPage() {
  const { dealId } = useParams<{ dealId: string }>()
  const navigate = useNavigate()
  const { deals, isLoadingDeals, loadDeals } = usePresalesDashboardStore()
  const { eventLinkSummary, fetchEventLinkSummary } = useCalendarStore()

  // Load deals if store is empty (direct navigation / page refresh)
  useEffect(() => {
    if (deals.length === 0) loadDeals()
    fetchEventLinkSummary()
  }, [])

  const deal = deals.find((d) => d.hubspot_deal_id === dealId)

  // Get event link data for this deal
  const dealEventData = eventLinkSummary?.by_deal.find((d) => d.deal_id === deal?.id)

  // --- Loading ---
  if (isLoadingDeals) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  // --- Not Found ---
  if (!deal) {
    return (
      <div className="space-y-6">
        <button
          onClick={() => navigate('/dashboard/presales/operations')}
          className="flex items-center gap-2 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Operational Overview
        </button>
        <Card className="p-8 text-center">
          <p className="text-[var(--text-muted)]">Deal not found</p>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            The deal with ID <code className="px-1.5 py-0.5 bg-[var(--bg-secondary)] rounded text-xs">{dealId}</code> could not be found.
          </p>
        </Card>
      </div>
    )
  }

  const statusInfo = getStatusInfo(deal)
  const dealTypeBadge = getDealTypeBadge(deal.dealtype)
  const salesHealth = getSalesHealth(deal)
  const presalesHealth = getPresalesHealth(deal)
  const presalesHrs = dealEventData ? (dealEventData.presales_minutes / 60).toFixed(1) : deal.total_presales_hours > 0 ? deal.total_presales_hours.toFixed(1) : '0'
  const f1Hrs = dealEventData ? (dealEventData.frankieone_total_minutes / 60).toFixed(1) : null

  return (
    <div className="space-y-6">
      {/* ========== HEADER ========== */}
      <div className="flex items-start gap-4">
        <button
          onClick={() => navigate('/dashboard/presales/operations')}
          className="mt-1 p-1.5 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-xl font-semibold text-[var(--text-primary)] truncate">{deal.deal_name}</h1>
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusInfo.classes}`}>
              {statusInfo.label}
            </span>
            {dealTypeBadge && (
              <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${dealTypeBadge.classes}`}>
                {dealTypeBadge.label}
              </span>
            )}
          </div>
          {deal.company_name && (
            <p className="text-sm text-[var(--text-muted)] mt-0.5">{deal.company_name}</p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {deal.dealhub_deal_id && (
            <a
              href={`/scope?session=${deal.dealhub_deal_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-[var(--button-primary-bg)] text-white hover:bg-[var(--button-primary-hover)] transition-colors"
            >
              Open SOW
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
          <a
            href={`https://app.hubspot.com/contacts/8836420/deal/${deal.hubspot_deal_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-[var(--border-color)] text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] transition-colors"
          >
            HubSpot
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>

      {/* ========== KEY METRICS ROW ========== */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          icon={<DollarSign className="h-4 w-4" />}
          label="Deal Value"
          value={formatCurrency(deal.amount)}
          subtext={deal.forecast_amount && deal.forecast_amount !== deal.amount ? `Forecast: ${formatCurrency(deal.forecast_amount)}` : undefined}
        />
        <MetricCard
          icon={<Clock className="h-4 w-4" />}
          label="Days Open"
          value={deal.days_open != null ? `${deal.days_open}` : '-'}
          subtext={deal.presales_engagement_date ? `Engaged: ${formatDate(deal.presales_engagement_date)}` : undefined}
        />
        <MetricCard
          icon={<BarChart3 className="h-4 w-4" />}
          label="Presales Hours"
          value={`${presalesHrs}h`}
          subtext={f1Hrs ? `FrankieOne total: ${f1Hrs}h` : undefined}
        />
        <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] p-4">
          <div className="flex items-center gap-2 text-[var(--text-muted)] mb-1">
            <Target className="h-4 w-4" />
            <span className="text-xs font-medium uppercase tracking-wide">Scope Progress</span>
          </div>
          <div className="text-2xl font-semibold text-[var(--text-primary)]">
            {deal.scope_completion_percent ?? 0}%
          </div>
          <div className="mt-2 w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${
                (deal.scope_completion_percent ?? 0) === 100
                  ? 'bg-green-500'
                  : (deal.scope_completion_percent ?? 0) >= 50
                  ? 'bg-blue-500'
                  : 'bg-yellow-500'
              }`}
              style={{ width: `${deal.scope_completion_percent ?? 0}%` }}
            />
          </div>
        </div>
      </div>

      {/* ========== DEAL DETAILS + HEALTH ========== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pipeline & Ownership */}
        <Card className="p-5">
          <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-4 uppercase tracking-wide">Pipeline & Ownership</h2>
          <dl className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-3 text-sm">
            <DetailRow label="Stage" value={deal.dealstage || deal.deal_stage} />
            <DetailRow label="Pipeline" value={deal.pipeline} />
            <DetailRow label="Deal Owner" value={deal.deal_owner_name} />
            <DetailRow label="Presales Engineer" value={deal.presales_engineer} />
            <DetailRow label="Solutions Consultant" value={deal.solutions_consultant_owner} />
            <DetailRow label="Presales Status" value={deal.presales_status} />
            <DetailRow label="Engagement Date" value={formatDate(deal.presales_engagement_date)} />
            <DetailRow label="Close Date" value={formatDate(deal.close_date)} />
            <DetailRow label="Created" value={formatDate(deal.create_date)} />
            <DetailRow label="Last Activity" value={formatDate(deal.last_activity_date)} />
          </dl>
        </Card>

        {/* Health Indicators */}
        <Card className="p-5">
          <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-4 uppercase tracking-wide">Health Indicators</h2>
          <div className="space-y-6">
            <HealthChecklist title="Sales Health" health={salesHealth} />
            <HealthChecklist title="Presales Health" health={presalesHealth} />
          </div>
        </Card>
      </div>

      {/* ========== HOURS BREAKDOWN ========== */}
      <Card className="p-5">
        <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-4 uppercase tracking-wide">Presales Hours Breakdown</h2>
        <HoursBreakdown deal={deal} dealEventData={dealEventData} />
      </Card>

      {/* ========== TASKS ========== */}
      <TasksSection deal={deal} />

      {/* ========== SCOPE & SOW + ENGAGEMENT ========== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Scope & SOW */}
        <Card className="p-5">
          <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-4 uppercase tracking-wide">Scope & SOW Status</h2>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <span className="text-sm text-[var(--text-muted)]">Scope Status:</span>
              <ScopeStatusBadge status={deal.scope_status} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-[var(--text-muted)]">Scope Completion</span>
                <span className="text-sm font-medium text-[var(--text-primary)]">{deal.scope_completion_percent ?? 0}%</span>
              </div>
              <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    (deal.scope_completion_percent ?? 0) === 100
                      ? 'bg-green-500'
                      : (deal.scope_completion_percent ?? 0) >= 50
                      ? 'bg-blue-500'
                      : 'bg-yellow-500'
                  }`}
                  style={{ width: `${deal.scope_completion_percent ?? 0}%` }}
                />
              </div>
            </div>
            {presalesHealth.sowCompletion != null && presalesHealth.sowCompletion > 0 && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-[var(--text-muted)]">SOW Completion</span>
                  <span className="text-sm font-medium text-[var(--text-primary)]">{presalesHealth.sowCompletion}%</span>
                </div>
                <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all ${
                      presalesHealth.sowCompletion === 100 ? 'bg-green-500' : presalesHealth.sowCompletion >= 50 ? 'bg-blue-500' : 'bg-yellow-500'
                    }`}
                    style={{ width: `${presalesHealth.sowCompletion}%` }}
                  />
                </div>
              </div>
            )}
            {deal.dealhub_deal_id && (
              <a
                href={`/scope?session=${deal.dealhub_deal_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-[var(--button-primary-bg)] hover:text-[var(--button-primary-hover)] transition-colors mt-2"
              >
                Open SOW
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        </Card>

        {/* Engagement Activity */}
        <Card className="p-5">
          <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-4 uppercase tracking-wide">Engagement Activity</h2>
          <div className="grid grid-cols-3 gap-4">
            <EngagementStat
              icon={<Mail className="h-5 w-5 text-blue-400" />}
              label="Emails"
              value={deal.num_emails || 0}
            />
            <EngagementStat
              icon={<CalendarDays className="h-5 w-5 text-green-400" />}
              label="Meetings"
              value={deal.num_meetings || 0}
            />
            <EngagementStat
              icon={<Presentation className="h-5 w-5 text-purple-400" />}
              label="Demos"
              value={dealEventData?.demo_count ?? deal.num_demos ?? 0}
            />
          </div>
          {deal.presales_demo_count > 0 && (
            <div className="mt-4 pt-4 border-t border-[var(--border-color)]">
              <span className="text-xs text-[var(--text-muted)]">
                Presales Demo Count (HubSpot): {deal.presales_demo_count}
              </span>
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}

// ─── Sub-components ───────────────────────────────────────────────

function MetricCard({ icon, label, value, subtext }: { icon: React.ReactNode; label: string; value: string; subtext?: string }) {
  return (
    <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] p-4">
      <div className="flex items-center gap-2 text-[var(--text-muted)] mb-1">
        {icon}
        <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <div className="text-2xl font-semibold text-[var(--text-primary)]">{value}</div>
      {subtext && <div className="text-xs text-[var(--text-muted)] mt-1">{subtext}</div>}
    </div>
  )
}

function DetailRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <>
      <dt className="text-[var(--text-muted)]">{label}</dt>
      <dd className="text-[var(--text-primary)]">{value || '-'}</dd>
    </>
  )
}

function HealthChecklist({ title, health }: { title: string; health: HealthResult }) {
  const colorClass = health.percentage >= 100 ? 'text-green-400' : health.percentage >= 66 ? 'text-yellow-400' : 'text-red-400'

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-[var(--text-secondary)]">{title}</h3>
        <span className={`text-sm font-semibold ${colorClass}`}>{health.score}/{health.total}</span>
      </div>
      <div className="space-y-2">
        {health.indicators.map((ind, idx) => (
          <div key={idx} className="flex items-center gap-2 text-sm">
            {ind.completed ? (
              <CheckCircle2 className="h-4 w-4 text-green-400 flex-shrink-0" />
            ) : (
              <XCircle className="h-4 w-4 text-red-400/60 flex-shrink-0" />
            )}
            <span className={ind.completed ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)]'}>
              {ind.label}
            </span>
          </div>
        ))}
      </div>
      {health.sowCompletion != null && health.sowCompletion > 0 && (
        <div className="mt-2 text-xs text-[var(--text-muted)]">SOW: {health.sowCompletion}% complete</div>
      )}
    </div>
  )
}

function HoursBreakdown({ dealEventData }: { deal: any; dealEventData: any }) {
  // Build category breakdown from calendar tag data (by_category on the deal's event links)
  const calendarCategories = dealEventData?.by_category || []
  const calendarCatMap = new Map<string, number>()
  for (const cat of calendarCategories) {
    calendarCatMap.set(cat.category, (cat.presales_minutes || 0) / 60)
  }

  // Map calendar tag categories to display labels
  // Calendar tags: Demo, Prep, Discovery, Technical, Implementations
  const CATEGORY_COLORS: Record<string, string> = {
    'Discovery': 'bg-blue-500',
    'Prep': 'bg-amber-500',
    'Demo': 'bg-green-500',
    'Technical': 'bg-purple-500',
    'Implementations': 'bg-rose-500',
  }

  const categories = [
    { label: 'Discovery', tag: 'Discovery', hours: calendarCatMap.get('Discovery') || 0 },
    { label: 'Prep', tag: 'Prep', hours: calendarCatMap.get('Prep') || 0 },
    { label: 'Demo', tag: 'Demo', hours: calendarCatMap.get('Demo') || 0 },
    { label: 'Technical', tag: 'Technical', hours: calendarCatMap.get('Technical') || 0 },
    { label: 'Implementations', tag: 'Implementations', hours: calendarCatMap.get('Implementations') || 0 },
  ]

  // Include any extra categories not in the standard list
  for (const [tag, hours] of calendarCatMap) {
    if (!categories.find(c => c.tag === tag)) {
      categories.push({ label: tag, tag, hours })
    }
  }

  const totalCalendar = categories.reduce((sum, c) => sum + c.hours, 0)
  const maxHours = Math.max(...categories.map((c) => c.hours), 0.1)
  const hasAnyHours = totalCalendar > 0

  const calendarPresalesHrs = dealEventData ? dealEventData.presales_minutes / 60 : 0
  const calendarF1Hrs = dealEventData ? dealEventData.frankieone_total_minutes / 60 : 0

  return (
    <div className="space-y-4">
      {/* Category bars */}
      {hasAnyHours ? (
        <div className="space-y-3">
          {categories.filter(c => c.hours > 0).map((cat) => (
            <div key={cat.tag}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-[var(--text-secondary)]">{cat.label}</span>
                <span className="text-sm font-mono text-[var(--text-primary)]">{cat.hours.toFixed(1)}h</span>
              </div>
              <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${CATEGORY_COLORS[cat.tag] || 'bg-blue-500'}`}
                  style={{ width: `${(cat.hours / maxHours) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-[var(--text-muted)] italic">No tagged calendar events yet. Tag events from the Calendar tab to see hours breakdown.</p>
      )}

      {/* Totals */}
      <div className="pt-3 border-t border-[var(--border-color)] flex flex-wrap gap-x-8 gap-y-2 text-sm">
        <div>
          <span className="text-[var(--text-muted)]">Tagged Total: </span>
          <span className="font-semibold text-[var(--text-primary)]">{totalCalendar.toFixed(1)}h</span>
        </div>
        {calendarPresalesHrs > 0 && (
          <div>
            <span className="text-[var(--text-muted)]">Calendar Presales: </span>
            <span className="font-semibold text-[var(--text-primary)]">{calendarPresalesHrs.toFixed(1)}h</span>
          </div>
        )}
        {calendarF1Hrs > 0 && (
          <div>
            <span className="text-[var(--text-muted)]">Calendar FrankieOne: </span>
            <span className="font-semibold text-[var(--text-primary)]">{calendarF1Hrs.toFixed(1)}h</span>
          </div>
        )}
        {dealEventData?.demo_count > 0 && (
          <div>
            <span className="text-[var(--text-muted)]">Demos (Calendar): </span>
            <span className="font-semibold text-[var(--text-primary)]">{dealEventData.demo_count}</span>
          </div>
        )}
      </div>
    </div>
  )
}

function ScopeStatusBadge({ status }: { status?: string }) {
  if (status === 'sent_to_customer') {
    return (
      <span className="inline-block px-2 py-1 text-xs rounded-full bg-green-500/10 text-green-600 border border-green-500/30">
        Sent
      </span>
    )
  }
  if (status === 'draft') {
    return (
      <span className="inline-block px-2 py-1 text-xs rounded-full bg-yellow-500/10 text-yellow-600 border border-yellow-500/30">
        Draft
      </span>
    )
  }
  return (
    <span className="inline-block px-2 py-1 text-xs rounded-full bg-gray-500/10 text-gray-600 border border-gray-500/30">
      Not Started
    </span>
  )
}

function EngagementStat({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="text-center p-3 rounded-lg bg-[var(--bg-secondary)]">
      <div className="flex justify-center mb-2">{icon}</div>
      <div className="text-2xl font-semibold text-[var(--text-primary)]">{value}</div>
      <div className="text-xs text-[var(--text-muted)] mt-0.5">{label}</div>
    </div>
  )
}
